import React from 'react'
import {
  IconCheckDefault,
  IconCloseDefault,
  IconInfoDefault,
} from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { TableFeature } from '@mc/plans-ui'
import { trackCertificatesRowClick } from '../../events/pageClick'
import { trackCertificatesRowViewed } from '../../events/rowViewed'
import { MC_PRO_TIER_I18N_NAMESPACE, i18nInstance } from '../../i18n'
import { FAQ_UUID } from '../CertificatesFAQ'

export type CertificatesTierValue =
  | 'included'
  | 'notIncluded'
  | { text: string; caption?: string; emphasized?: boolean }

export interface CertificatesFeatureProps {
  title: string
  description: string
  values:
    | [CertificatesTierValue, CertificatesTierValue, CertificatesTierValue]
    | [
        CertificatesTierValue,
        CertificatesTierValue,
        CertificatesTierValue,
        CertificatesTierValue,
      ]
  rowPositionIndex?: number
}

const TIER_CELLS = [
  TableFeature.Individual,
  TableFeature.Duo,
  TableFeature.Family,
  TableFeature.FamilyPlus,
]

const openCertificatesFAQ = () => {
  trackCertificatesRowClick()

  if (typeof window === 'undefined') return

  const heading = window.document.querySelector<HTMLElement>(
    `[data-uuid="${FAQ_UUID}"]`,
  )
  if (!heading) return

  const scrollToHeading = () => heading.scrollIntoView({ behavior: 'smooth' })

  const button = heading.querySelector<HTMLButtonElement>(
    'button[aria-expanded="false"]',
  )
  if (button) {
    button.focus({ preventScroll: true })
    button.click()
    // Scrolling during the accordion expansion (~200ms) interrupts the smooth scroll.
    window.setTimeout(scrollToHeading, 250)
  } else {
    scrollToHeading()
  }
}

export const CertificatesTitle = ({ title }: { title: string }) => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)

  return (
    <span className='d-flex align-items-center flex-gap-1'>
      {title}
      <button
        style={{ lineHeight: '0' }}
        className='mc-clickable'
        onClick={openCertificatesFAQ}
      >
        <IconInfoDefault size={2} srText={t('matrix.certificates.info')} />
      </button>
    </span>
  )
}

export const CertificatesValue = ({
  value,
}: {
  value: CertificatesTierValue
}) => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)

  if (value === 'included') {
    return (
      <IconCheckDefault size={2} srText={t('matrix.certificates.included')} />
    )
  }

  if (value === 'notIncluded') {
    return (
      <IconCloseDefault
        size={2}
        srText={t('matrix.certificates.notIncluded')}
      />
    )
  }

  return (
    <span className='d-flex flex-column'>
      <span className={value.emphasized ? 'mc-text--bold' : undefined}>
        {value.text}
      </span>
      {value.caption && <span className='mc-text-small'>{value.caption}</span>}
    </span>
  )
}

export const CertificatesFeature = withI18nContext(i18nInstance)(({
  title,
  description,
  values,
  rowPositionIndex = 2,
}: CertificatesFeatureProps) => {
  const spacing = 12 / values.length

  return (
    <TableFeature.Feature
      onFirstVisible={() => trackCertificatesRowViewed(rowPositionIndex)}
    >
      <TableFeature.Label
        title={<CertificatesTitle title={title} />}
        subtitle={description}
      />
      {values.map((value, index) => {
        const Cell = TIER_CELLS[index]

        return (
          <Cell key={index} spacing={spacing}>
            <CertificatesValue value={value} />
          </Cell>
        )
      })}
    </TableFeature.Feature>
  )
})
